import { DestroyRef, Inject, Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  SaveUserDevice,
  SaveUserDeviceRespose,
  UserDevice,
  UserDevicesPlatform,
  UserDevicesType,
} from '../../interfaces/user-device';
import { LogEvent } from '../../enums/log-event';
import { LoggerService } from '../logger.service';
import { environment } from '../../../../environments/environment';
import { Observable, catchError, of } from 'rxjs';
import {
  DeviceDetector,
  DEVICE_DETECTOR,
} from 'src/app/shared/interfaces/device-detector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../user.service';
import { UserNotificationStateService } from './user-notification-state.service';

@Injectable({
  providedIn: 'root',
})
export class UserDevicesService {
  private userDevice!: UserDevice;
  private destroyRef = inject(DestroyRef);

  constructor(
    private logger: LoggerService,
    private userService: UserService,
    @Inject(DEVICE_DETECTOR) private deviceDetectorService: DeviceDetector,
    private httpClient: HttpClient,
    private userNotificationStateService: UserNotificationStateService
  ) {
    this.userNotificationStateService.userDeviceChangeStatus$.subscribe(
      (changeStatusTo: boolean | null) => {
        if (changeStatusTo === null || !this.userDevice) return;

        changeStatusTo
          ? this.saveToken(this.userDevice.token, true)
          : this.deleteToken();
      }
    );
    this.userNotificationStateService.isIOS = this.isIOS();
    this.userNotificationStateService.isStandalone = this.isStandalone();
  }

  saveToken(token: string, isActive?: boolean) {
    const device = this.generateDeviceData(token);
    isActive && (device.isActive = isActive);

    this.userNotificationStateService.setIsLoadingUserDevice(true);
    this.save(device)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.userNotificationStateService.setIsLoadingUserDevice(false);
          this.userNotificationStateService.setNotificationRegisterError(
            'on_save_user_device'
          );
          this.logger.error(
            LogEvent.LIVECHAT_NOTIFICATION_SAVE_TOKEN_FAILED,
            error as unknown as Error
          );
          return of(null);
        })
      )
      .subscribe((savedUserDevice) => {
        if (!savedUserDevice) return;

        this.userDevice = this.generateUserDevice(savedUserDevice);
        this.userNotificationStateService.setIsLoadingUserDevice(false);
        this.userNotificationStateService.setUserDeviceCurrentStatus(
          this.userDevice.isActive
        );
      });
  }

  deleteToken() {
    if (this.userDevice) {
      this.userNotificationStateService.setIsLoadingUserDevice(true);
      this.delete(this.userDevice.userDeviceId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError((error) => {
            this.userNotificationStateService.setIsLoadingUserDevice(false);
            this.logger.error(
              LogEvent.LIVECHAT_NOTIFICATION_DELETE_TOKEN_FAILED,
              error as unknown as Error
            );
            return of(null);
          })
        )
        .subscribe((savedUserDevice) => {
          if (!savedUserDevice) return;

          this.userDevice = this.generateUserDevice(savedUserDevice);
          this.userNotificationStateService.setUserDeviceCurrentStatus(
            this.userDevice.isActive
          );
          this.userNotificationStateService.setIsLoadingUserDevice(false);
        });
    }
  }

  isIOS() {
    return this.getOperationalSystem() === 'iOS';
  }

  isStandalone() {
    if (this.isIOS()) {
      return (window.navigator as any).standalone === true;
    }
    return window.matchMedia('(display-mode: standalone)').matches;
  }

  private save(device: SaveUserDevice): Observable<SaveUserDeviceRespose> {
    const body = !this.userService.isCurrentUserGlobal()
      ? {
          ...device,
          companiesIds: this.userService.getCurrentUser().companiesIds,
        }
      : device;
    return this.httpClient.post<SaveUserDeviceRespose>(
      `${environment.externalUrls.bff}/user/devices`,
      body
    );
  }

  private delete(id: number) {
    const body = !this.userService.isCurrentUserGlobal()
      ? { companiesIds: this.userService.getCurrentUser().companiesIds }
      : {};
    return this.httpClient.delete<SaveUserDeviceRespose>(
      `${environment.externalUrls.bff}/user/devices/${id}`,
      {
        body,
      }
    );
  }

  private getOperationalSystem() {
    return this.deviceDetectorService.getOperationalSystem();
  }

  private generateDeviceData(token: string): SaveUserDevice {
    const userAgent = this.deviceDetectorService.getUserAgent();
    return {
      deviceOs: this.getOperationalSystem(),
      userAgent: userAgent,
      token,
      type: this.deviceDetectorService.isDesktop(userAgent)
        ? UserDevicesType.DESKTOP
        : UserDevicesType.MOBILE,
      platform: UserDevicesPlatform.LIVECHAT,
    };
  }

  private generateUserDevice(
    savedUserDevice: SaveUserDeviceRespose
  ): UserDevice {
    this.deviceDetectorService.setDeviceInfo(savedUserDevice.userAgent);

    const {
      os,
      browser,
      browser_version: browserVersion,
    } = this.deviceDetectorService.getDeviceInfo();

    return {
      userDeviceId: savedUserDevice.idUserDevice,
      userId: savedUserDevice.idUser,
      os,
      browser,
      browserVersion,
      userAgent: savedUserDevice.userAgent,
      token: savedUserDevice.token,
      type: savedUserDevice.type,
      lastAccess: new Date(savedUserDevice.lastAccess),
      createdAt: new Date(savedUserDevice.createdAt),
      updatedAt: new Date(savedUserDevice.updatedAt),
      isActive: savedUserDevice.isActive,
      platform: savedUserDevice.platform,
    };
  }
}
